
const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const sampleFeedbacks = [
    "This project really pushed my understanding of the topic.",
    "Documentation was a bit scarce, but the challenge was worth it.",
    "Make sure to handle all edge cases, especially memory leaks!",
    "Great introduction to low-level concepts.",
    "The evaluation process was smooth. Learned a lot from my peers.",
    "Hardest part was the parsing, but the logic came together nicely.",
    "Don't underestimate the time needed for the bonus part.",
    "A classic 42 project. Frustrating but rewarding.",
    "Check the subject carefully, I missed a small detail and failed.",
    "Use Valgrind! It saved my life multiple times."
];

const replySamples = [
    "Agreed, that part was tricky.",
    "Thanks for the tip!",
    "I had the same issue.",
    "Could you share your resources?",
    "Valgrind is a lifesaver indeed."
];

const users = ["dkhalfao", "aagharbi", "sel-bech", "maddam", "obajali", "jabouhni"];

const generateReplies = () => {
    if (Math.random() > 0.6) return [];
    const numReplies = getRandomInt(1, 2);
    return Array.from({ length: numReplies }, () => ({
        user: users[getRandomInt(0, users.length - 1)],
        comment: replySamples[getRandomInt(0, replySamples.length - 1)],
        date: "Recently"
    }));
};

const generateFeedbacks = () => {
    const numFeedbacks = getRandomInt(1, 5);
    const shuffledFeedbacks = [...sampleFeedbacks].sort(() => 0.5 - Math.random());
    return Array.from({ length: numFeedbacks }, (_, i) => ({
        user: users[getRandomInt(0, users.length - 1)],
        rating: getRandomInt(3, 5),
        comment: shuffledFeedbacks[i % shuffledFeedbacks.length],
        date: `${getRandomInt(1, 11)} months ago`,
        replies: generateReplies()
    }));
};

const generateCompletedBy = () => {
    const numUsers = getRandomInt(3, 8);
    const pool = [...users, "driss", "alex", "sarah", "mike", "lisa", "john", "jane"].sort(() => 0.5 - Math.random());
    return pool.slice(0, numUsers).map(u => ({
        user: u,
        date: `${getRandomInt(1, 30)} days ago`,
        avatar_color: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'][getRandomInt(0, 4)]
    }));
};

const generateInProgress = () => {
    const numUsers = getRandomInt(2, 6);
    const pool = [...users, "alex", "sarah", "mike", "lisa"].sort(() => 0.5 - Math.random());
    return pool.slice(0, numUsers).map(u => ({
        user: u,
        date: `Started ${getRandomInt(1, 15)} days ago`,
        avatar_color: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'][getRandomInt(0, 4)]
    }));
};

// 42 New Common Core – Project Definitions (2026)
export const newCommonCoreProjects = [
    {
        id: "libft",
        title: "Libft",
        level: 0,
        rating: 4.8,
        category: "C",
        description: "Libft is the foundational project of the 42 curriculum and your first real step into writing reliable C. The goal is to build your own mini standard library by re-implementing core libc-style utilities: string and memory functions, character checks, and data structures like linked lists. Beyond re-coding functions, the real challenge is correctness and consistency: careful pointer arithmetic, strict handling of edge cases, and zero memory leaks. When you finish, you don’t just pass a project—you create a personal toolbox that you will reuse in almost every future C assignment.",
        rank: "Rank 00",
        rank_order: 0,
        learning_objectives: [
            "How memory works in C (heap vs stack)",
            "Pointer manipulation",
            "Manual memory allocation and deallocation",
            "Reusable, clean, well-structured code",
            "Building a personal C toolbox"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "push_swap",
        title: "push_swap",
        level: 2,
        rating: 4.4,
        category: "Algorithms",
        description: "push_swap is an algorithm and optimization challenge disguised as a simple sorting task. You must sort a stack of integers using only a tiny set of allowed operations (push, swap, rotate, reverse-rotate) and the grading focuses heavily on efficiency: not just getting a correct sort, but producing as few operations as possible. You’ll compare strategies, think in terms of complexity, and learn how constraints change the “best” algorithm. This project is where many students first feel what performance engineering looks like.",
        rank: "Rank 01",
        rank_order: 1,
        learning_objectives: [
            "Algorithmic thinking",
            "Time and space complexity",
            "Sorting strategies (chunking, radix sort)",
            "Optimization under constraints"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "ft_printf",
        title: "ft_printf",
        level: 1,
        rating: 4.6,
        category: "C",
        description: "ft_printf is a reimplementation of the famous printf function and a deep dive into how formatting engines work. You parse a format string, manage a variable list of arguments, and produce correct output for multiple specifiers (characters, strings, integers, hex, pointers, etc.). A strong solution requires clean parsing, good decomposition, and careful handling of flags, width, precision, and corner cases. This project strengthens your understanding of variadic functions, low-level output, and building an extensible, testable codebase.",
        rank: "Rank 01",
        rank_order: 1,
        learning_objectives: [
            "Variadic arguments (va_list)",
            "Parsing complex input formats",
            "Low-level output control",
            "Robust error handling",
            "Understanding how high-level functions are built"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "get_next_line",
        title: "get_next_line (GNL)",
        level: 1,
        rating: 4.5,
        category: "C",
        description: "get_next_line is about building a robust line reader that works with any file descriptor. The function must return exactly one line per call, correctly handling partial reads, buffering, and end-of-file conditions. The tricky part is state: you need to keep leftovers between calls without corrupting output, and you must manage memory carefully to avoid leaks or invalid frees. This project teaches real-world input handling patterns you’ll reuse in parsers, network clients, and general systems programming.",
        rank: "Rank 01",
        rank_order: 1,
        learning_objectives: [
            "File descriptor behavior",
            "Static variables",
            "Buffer management",
            "Edge-case handling (EOF, errors, multiple FDs)"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "b2br",
        title: "B2BR",
        level: 2,
        rating: 4.2,
        category: "C",
        description: "B2BR is a logic and problem-solving focused C project designed to push you beyond syntax and into engineering thinking. You’ll translate a precise specification into a dependable implementation, handling constraints, validating inputs, and designing clean interfaces. The project rewards structured decomposition: break the problem into small testable parts, keep invariants clear, and write defensive code that behaves correctly under stress. It’s less about “one clever trick” and more about building something correct, maintainable, and resilient.",
        rank: "Rank 02",
        rank_order: 2,
        learning_objectives: [
            "Logical reasoning",
            "Structured problem decomposition",
            "Defensive programming",
            "Clean API design"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "piscine_python",
        title: "Piscine Python",
        level: 1,
        rating: 4.7,
        category: "Python",
        description: "The Python Piscine introduces Python as a high-level language and shifts your mindset from manual memory management to clear, expressive abstraction. You’ll practice Python syntax, standard library usage, and the fundamentals of clean program structure. A major focus is Object-Oriented Programming: classes, methods, inheritance, and designing interfaces that stay readable as a project grows. By the end, you should be comfortable writing Python that is idiomatic, tested, and easier to evolve than low-level C implementations.",
        rank: "Rank 02",
        rank_order: 2,
        learning_objectives: [
            "Python syntax and semantics",
            "Classes, objects, inheritance",
            "Exception handling",
            "Clean and expressive code design"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "a_maze_ing",
        title: "a_maze_ing",
        level: 2,
        rating: 4.3,
        category: "Python",
        description: "a_maze_ing connects algorithms to visuals: you generate and/or solve mazes using classic graph traversal techniques, then render the process using Python graphics libraries. The core is algorithmic—DFS, BFS, A*, or variants—while the fun part is watching it come to life with animation and interaction. You’ll learn how to represent problems as graphs, how to reason about paths and heuristics, and how to structure event-driven rendering loops so your algorithm and UI stay cleanly separated.",
        rank: "Rank 02",
        rank_order: 2,
        learning_objectives: [
            "Graph traversal algorithms (DFS, BFS, A*)",
            "Visualization techniques",
            "Event-driven programming",
            "Algorithm-to-graphics translation"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "codexion",
        title: "codexion",
        level: 3,
        rating: 4.1,
        category: "Python",
        description: "codexion is an introduction to concurrency: building programs where multiple tasks run at the same time and still behave correctly. In Python you’ll explore threads and/or processes, learn where race conditions come from, and practice synchronization with locks, queues, and other coordination tools. The project forces you to think about correctness under interleaving—what happens when operations overlap—and how to design systems that stay stable under load. This is a key step toward real-world systems work.",
        rank: "Rank 03",
        rank_order: 3,
        learning_objectives: [
            "Threads vs processes",
            "Race conditions",
            "Synchronization mechanisms",
            "Deadlocks and safe concurrency design"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "fly_in",
        title: "fly_in",
        level: 3,
        rating: 4.0,
        category: "Python",
        description: "fly_in focuses on algorithmic problem solving with an emphasis on efficiency and elegance. The challenges are designed so a brute-force approach quickly becomes too slow, pushing you to choose the right data structures and optimize the critical path. You’ll practice spotting patterns, reducing complexity, and writing solutions that scale as input grows. This project is excellent training for interviews and for building the “algorithmic instincts” you’ll need in later systems and AI projects.",
        rank: "Rank 03",
        rank_order: 3,
        learning_objectives: [
            "Problem-solving speed",
            "Algorithm optimization",
            "Data structures usage",
            "Strategic thinking"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "call_me_maybe",
        title: "call_me_maybe",
        level: 3,
        rating: 4.2,
        category: "AI",
        description: "call_me_maybe is your first structured exposure to AI-style thinking. Rather than focusing on heavy math, you build intelligent behavior using rules, heuristics, scoring functions, and simple predictive logic. The real learning is in modeling: define signals, decide what information matters, and make decisions that look “smart” under constraints. It’s a bridge between classic algorithms and modern AI projects, teaching you how to reason about behavior, trade-offs, and evaluation.",
        rank: "Rank 03",
        rank_order: 3,
        learning_objectives: [
            "Decision-making systems",
            "Pattern recognition",
            "Simple prediction models",
            "AI reasoning foundations"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "net_practice",
        title: "net_practice",
        level: 2,
        rating: 4.6,
        category: "Networking",
        description: "net_practice teaches practical networking fundamentals by making you configure IP addressing, subnet masks, gateways, and routing rules. You’ll build intuition for the TCP/IP model, how packets find their destination, and how subnetting affects what can communicate with what. The exercises are hands-on and immediately useful for debugging real systems, understanding containers and cloud networks, and reading network diagrams. It’s one of the best “theory meets practice” projects in the curriculum.",
        rank: "Rank 04",
        rank_order: 4,
        learning_objectives: [
            "TCP/IP model",
            "Subnetting",
            "Network masks",
            "Real-world networking logic"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "pacman",
        title: "pacman",
        level: 2,
        rating: 4.4,
        category: "Python",
        description: "pacman is a game development project where you recreate (or extend) the classic Pac-Man experience using Python. You’ll implement a game loop, handle input and timing, render graphics, and manage state transitions (menus, playing, win/lose). The project also introduces simple AI behaviors for enemies, collision detection, and balancing game rules so the experience feels right. It’s a fun way to practice architecture: keeping logic, rendering, and assets organized.",
        rank: "Rank 04",
        rank_order: 4,
        learning_objectives: [
            "Game loops",
            "Collision detection",
            "State management",
            "AI behavior for enemies",
            "Graphical rendering"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "rag_against_the_machine",
        title: "RAG against the machine",
        level: 4,
        rating: 4.5,
        category: "AI",
        description: "RAG against the machine introduces modern AI system design with Retrieval-Augmented Generation (RAG): combining a large language model with external knowledge so answers can be grounded in real documents. You’ll build a pipeline that chunks text, creates embeddings, stores vectors in a database, retrieves relevant context at query time, and injects it into the model prompt. The focus is practical engineering: precision vs recall, latency, evaluation, and safe prompt construction. This project mirrors real industry AI assistants.",
        rank: "Rank 04",
        rank_order: 4,
        learning_objectives: [
            "Vector databases",
            "Embeddings",
            "Retrieval pipelines",
            "Context injection into AI models"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "inception",
        title: "inception",
        level: 4,
        rating: 4.3,
        category: "DevOps",
        description: "inception is a DevOps-focused project where you build and orchestrate a multi-service application using Docker. You’ll write Dockerfiles, define services, configure container networks, manage volumes, and enforce repeatable infrastructure. The goal is to understand how modern applications are deployed: isolated services, controlled dependencies, and automation-friendly environments. By the end, you’ll be more than a developer—you’ll be able to reason about systems architecture, deployment, and reliability.",
        rank: "Rank 05",
        rank_order: 5,
        learning_objectives: [
            "Docker & Dockerfiles",
            "Container networking",
            "Service orchestration",
            "Infrastructure automation"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "the_answer_protocol",
        title: "the_answer_protocol",
        level: 4,
        rating: 4.1,
        category: "Networking",
        description: "the_answer_protocol is about designing and implementing a custom network protocol from the ground up. You’ll define message formats, encoding rules, state transitions, validation, and error handling—then build client/server components that speak your protocol reliably. The key challenge is robustness: handling malformed messages, partial reads, compatibility concerns, and ensuring the protocol stays secure and consistent. This project feels like real protocol engineering and strengthens your understanding of networking beyond HTTP.",
        rank: "Rank 05",
        rank_order: 5,
        learning_objectives: [
            "Protocol structure",
            "Message validation",
            "Client-server communication",
            "Reliability and security principles"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "agent_smith",
        title: "agent_smith",
        level: 5,
        rating: 4.6,
        category: "AI",
        description: "agent_smith focuses on building autonomous AI agents: systems that can plan actions, use tools, interact with an environment, and adjust behavior based on outcomes. You’ll work with agent architectures (planning loops, memory, tool invocation), design evaluation criteria, and handle safety constraints so the agent stays reliable. The project is advanced because it combines software engineering with reasoning: you’re not just writing functions, you’re building a decision-making system that must operate under uncertainty.",
        rank: "Rank 05",
        rank_order: 5,
        learning_objectives: [
            "Agent architectures",
            "Planning and reasoning",
            "Tool usage by AI",
            "Multi-agent systems"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
    {
        id: "ft_transcendence",
        title: "ft_transcendence",
        level: 6,
        rating: 4.8,
        category: "Web",
        description: "ft_transcendence is the final full-stack capstone (old promo style) where you ship a complete web product. You’ll design an application with authentication, real-time features, persistent storage, and a production-ready structure across frontend and backend. The difficulty is not one isolated concept—it’s integrating everything: clean API design, security basics, deployment thinking, and a user experience that feels coherent. This project validates that you can build and deliver an end-to-end system like a professional team would.",
        rank: "Rank 06",
        rank_order: 6,
        learning_objectives: [
            "Full-stack architecture",
            "Authentication and security basics",
            "Real-time communication",
            "Deploying a complete web product"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
    },
];

export const commonCoreProjects = [
    // Rank 0
    {
        id: "libft",
        title: "Libft",
        level: 0,
        rating: 4.8,
        category: "C Basics",
        description: "Your very first project at 42. This project is about coding a C library. It will contain a lot of general purpose functions your script will rely upon. The library includes string manipulation, memory management, and linked list handling functions that mirror the behavior of standard C library functions (libc). By rewriting them from scratch, you gain a foundational understanding of C's low-level operations.",
        rank: "Rank 00",
        learning_objectives: [
            "C standard library functions behavior",
            "Memory management (malloc, free)",
            "Pointer manipulation and arithmetic",
            "Linked lists data structure",
            "Makefile creation and usage"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
        
    },
    // Rank 1
    {
        id: "ft_printf",
        title: "ft_printf",
        level: 1,
        rating: 4.5,
        category: "Algorithms",
        description: "This project is pretty straightforward. You have to recode printf. You will learn what is and how to use variadic arguments. The versatility of the printf function in C represents a great exercise in programming. This project is of moderate difficulty. It will enable you to discover variadic functions in C. The key to a successful ft_printf is a well-structured and extensible codebase.",
        rank: "Rank 01",
        learning_objectives: [
            "Variadic arguments (va_start, va_arg, va_end)",
            "String formatting and parsing",
            "Structs and dispatch tables",
            "Buffer management",
            "Low-level I/O optimization"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
        
    },
    {
        id: "get_next_line",
        title: "get_next_line",
        level: 1,
        rating: 4.3,
        category: "IO/File",
        description: "May it be a file, stdin, or even later a network connection, you will need a way to read content line by line. This project will not only allow you to add a very convenient function to your collection, but it will also teach you a highly interesting new concept in C programming: static variables. You will deal with memory leaks, buffer management, and reading from different file descriptors.",
        rank: "Rank 01",
        learning_objectives: [
            "Static variables and their lifecycle",
            "File descriptors and read() system call",
            "Buffer management and dynamic allocation",
            "Memory leak prevention",
            "Edge case handling (empty files, huge lines)"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress(),
        

    },
    {
        id: "born2beroot",
        title: "Born2BeRoot",
        level: 1,
        rating: 4.0,
        category: "SysAdmin",
        description: "This project aims to introduce you to the wonderful world of virtualization. You will create your first machine in VirtualBox (or similar) and configure it. You will setup a secure server with strict rules, including partitioning, firewall setup, strong password policies, and monitoring scripts. This is your first step into System Administration.",
        rank: "Rank 01",
        learning_objectives: [
            "Virtualization (VirtualBox)",
            "LVM (Logical Volume Manager) partitioning",
            "SSH configuration and security",
            "UFW (Uncomplicated Firewall) setup",
            "User and Group management (sudo policy)",
            "Bash scripting for monitoring"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Rank 2
    {
        id: "push_swap",
        title: "push_swap",
        level: 2,
        rating: 4.2,
        category: "Algorithms",
        description: "This project will make you sort data on a stack, with a limited set of instructions, using the lowest possible number of actions. To succeed, you’ll have to manipulate various types of algorithms and choose the most appropriate solution (out of many) for an optimized data sorting. It forces you to think about complexity (Big O notation) and optimization.",
        rank: "Rank 02",
        learning_objectives: [
            "Sorting algorithms (Radix, Quick Sort, etc.)",
            "Stack data structure implementation",
            "Algorithmic complexity (Big O)",
            "Optimization techniques",
            "Problem-solving and logic"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "minitalk",
        title: "minitalk",
        level: 2,
        rating: 4.6,
        category: "UNIX",
        description: "The purpose of this project is to code a small data exchange program using UNIX signals. It is an introductory project for the bigger UNIX projects to come. You will create a client and server that communicate by sending strings encoded as bits solely via SIGUSR1 and SIGUSR2 signals. Handling signal asynchronicity is the main challenge.",
        rank: "Rank 02",
        learning_objectives: [
            "UNIX Signals (SIGUSR1, SIGUSR2)",
            "Process ID (PID) handling",
            "Bitwise operations",
            "Signal handling (sigaction)",
            "Client-Server architecture basics"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "pipex",
        title: "pipex",
        level: 2,
        rating: 4.1,
        category: "UNIX",
        description: "This project is the discovery in detail and by programming of a UNIX mechanism that you already know: the pipe. You will simulate the behavior of the shell pipe `|`. You will learn how to duplicate file descriptors, redirect streams, and execute commands using `execve`.",
        rank: "Rank 02",
        learning_objectives: [
            "Pipes and Redirections",
            "fork() and process creation",
            "execve() and program execution",
            "File descriptor manipulation (dup2)",
            "Process synchronization (waitpid)"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "so_long",
        title: "so_long",
        level: 2,
        rating: 4.7,
        category: "Graphics",
        description: "This project is a small 2D game to work with textures, sprites, and some other basic gameplay elements. You will create a simple game where a character collects items and escapes through an exit, navigating a map defined in a file. It uses the MiniLibX library.",
        rank: "Rank 02",
        learning_objectives: [
            "Window management (MiniLibX)",
            "Event handling (Keyboard, Mouse)",
            "Texture and Sprite rendering",
            "Map parsing and flood fill algorithm",
            "Game loop basics"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "fdf",
        title: "FdF",
        level: 2,
        rating: 4.4,
        category: "Graphics",
        description: "This project is about representing a landscape as a 3D wireframe model. You will learn to perform isometric projections and handle 3D coordinates. You will parse a map of coordinates and render a wireframe mesh, allowing basic transformations like zoom and rotation.",
        rank: "Rank 02",
        learning_objectives: [
            "3D Projection mathematics (Isometric)",
            "Bresenham's line algorithm",
            "Coordinate systems and transformations",
            "Event handling and user interaction",
            "Color gradients and rendering"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "fract-ol",
        title: "fract-ol",
        level: 2,
        rating: 4.8,
        category: "Graphics",
        description: "Discover 2D programming and the psychedelic universe of fractals. This project explores the mathematical concept of fractals like the Mandelbrot set and Julia set. You will explore the concept of the complex plane and infinite iteration to generate beautiful images.",
        rank: "Rank 02",
        learning_objectives: [
            "Complex numbers and the complex plane",
            "Mathematical optimization",
            "Event handling (Zoom, Pan)",
            "Color mapping and psychedelic rendering",
            "Performance optimization"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Rank 3
    {
        id: "philosophers",
        title: "Philosophers",
        level: 3,
        rating: 4.3,
        category: "Threading",
        description: "In this project, you will learn the basics of threading a process. You will see how to create threads and you will discover mutexes. The goal is to solve the classic Dining Philosophers problem where philosophers sit at a table and must eat without starving, sharing forks (mutexes). Avoiding deadlocks and race conditions is paramount.",
        rank: "Rank 03",
        learning_objectives: [
            "Multithreading (pthread_create, pthread_join)",
            "Synchronization primitives (Mutexes)",
            "Deadlock and Race Condition prevention",
            "Resource sharing and concurrency",
            "Time management in systems"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "minishell",
        title: "minishell",
        level: 3,
        rating: 4.9,
        category: "Shell",
        description: "As you might have guessed, the goal of this project is to create a simple shell. It’s a huge milestone. You will learn a lot about processes and file descriptors. You need to implement a command-line interface that supports history, pipes, redirections, signals, and built-in commands. It mimics bash.",
        rank: "Rank 03",
        learning_objectives: [
            "Process management and signaling",
            "Parsing and Lexical Analysis",
            "Abstract Syntax Trees (AST)",
            "File descriptors and Redirection",
            "Environment variable management"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Rank 4
    {
        id: "cub3d",
        title: "cub3D",
        level: 4,
        rating: 4.8,
        category: "Graphics",
        description: "This project is inspired by the world-famous Wolfenstein 3D game, which was the first FPS ever. It will enable you to explore ray-casting. Your goal is to make a dynamic view inside a maze, in which you’ll have to find your way.",
        rank: "Rank 04",
        learning_objectives: [
            "Raycasting algorithm",
            "Texture mapping and scaling",
            "Trigonometry and vector math",
            "Game engine architecture",
            "Collision detection"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "minirt",
        title: "miniRT",
        level: 4,
        rating: 4.7,
        category: "Graphics",
        description: "This project is an introduction to the beautiful world of Raytracing. Once simple Raytracing is mastered, you will be able to make great computer generated images. You will implement a ray tracer that can render simple geometric shapes/objects, light sources, and shadows.",
        rank: "Rank 04",
        learning_objectives: [
            "Ray tracing mathematics",
            "Vector algebra (Dot/Cross products)",
            "Lighting models (Ambient, Diffuse, Specular)",
            "Intersection algorithms (Sphere, Plane, Cylinder)",
            "Shadow rendering"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "netpractice",
        title: "NetPractice",
        level: 4,
        rating: 3.5,
        category: "Network",
        description: "This project is a general document to discover networking. You will practice configuring small scale networks. You will solve levels designed to make you understand how TCP/IP addressing works.",
        rank: "Rank 04",
        learning_objectives: [
            "TCP/IP Protocol Suite",
            "IP Addressing and Subnetting",
            "Routing tables and gateways",
            "Network configuration",
            "Troubleshooting connectivity"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "cpp00-04",
        title: "CPP Modules 00-04",
        level: 4,
        rating: 4.2,
        category: "C++",
        description: "The first set of C++ modules. Ideally, you should already know C. This project is here to help you understand the specificities of C++ compared to C. You will learn Object-Oriented Programming, classes, polymorphism, inheritance, and encapsulation.",
        rank: "Rank 04",
        learning_objectives: [
            "Object-Oriented Programming (OOP)",
            "Classes, Instances, and Member functions",
            "Polymorphism and Inheritance",
            "Operator overloading",
            "Memory management (new/delete)"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Rank 5
    {
        id: "cpp05-09",
        title: "CPP Modules 05-09",
        level: 5,
        rating: 4.1,
        category: "C++",
        description: "The second set of C++ modules. You will dive deeper into C++. You will learn about Templates, the Standard Template Library (STL), and Exceptions. This is the advanced part of the C++ curriculum.",
        rank: "Rank 05",
        learning_objectives: [
            "Templates and Generic Programming",
            "Standard Template Library (STL) Containers",
            "Exception handling (try/catch)",
            "C++ casts and type conversion",
            "Iterators and Algorithms"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "inception",
        title: "Inception",
        level: 5,
        rating: 4.5,
        category: "DevOps",
        description: "This project aims to broaden your knowledge of system administration by using Docker. You will virtualize several Docker images, creating them in your new personal virtual machine. You will build a complete infrastructure (WordPress, MariaDB, NGINX) using docker-compose.",
        rank: "Rank 05",
        learning_objectives: [
            "Docker and Containerization",
            "Docker Compose orchestration",
            "System Administration within containers",
            "Volume management and networking",
            "Service configuration (NGINX, MariaDB, WordPress)"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "webserv",
        title: "webserv",
        level: 5,
        rating: 4.6,
        category: "Web",
        description: "This project is about writing your own HTTP server. You will be able to test it with an actual browser. HTTP is one of the most used protocols on the internet. You will implement a server that is compatible with part of the HTTP/1.1 RFC.",
        rank: "Rank 05",
        learning_objectives: [
            "HTTP/1.1 Protocol specifications",
            "Socket programming (Non-blocking I/O)",
            "I/O Multiplexing (select/poll/epoll)",
            "Request parsing and Response generation",
            " CGI implementation"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "ft_irc",
        title: "ft_irc",
        level: 5,
        rating: 4.3,
        category: "Network",
        description: "This project is about creating your own IRC server. You will use an actual IRC client to test your server. You will deal with TCP/IP connections and implement the IRC protocol.",
        rank: "Rank 05",
        learning_objectives: [
            "IRC Protocol (RFC 1459/2812)",
            "TCP/IP Socket programming",
            "Client management and channels",
            "Authentication and Commands",
            "Non-blocking network I/O"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Rank 6
    {
        id: "ft_transcendence",
        title: "ft_transcendence",
        level: 6,
        rating: 4.9,
        category: "Web",
        description: "This project is about creating a website for the mighty Pong contest. You will use modern web development tools. You will create a SPA (Single Page Application) with a backend (NestJS) and a frontend (React/Vue/Angular), including a real-time game and chat.",
        rank: "Rank 06",
        learning_objectives: [
            "Single Page Application (SPA) architecture",
            "Backend development (NestJS)",
            "Database management (PostgreSQL)",
            "Real-time communication (WebSockets)",
            "OAuth authentication (42 API)"
        ],
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    }
];

export const piscineDays = [
    {
        id: "c00",
        title: "C00",
        level: 0,
        rating: 4.2,
        category: "Piscine C",
        description: "Introduction to C. The very first steps. Learning about write, variables, and basic output.",
        rank: "Week 1",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c01",
        title: "C01",
        level: 0,
        rating: 4.3,
        category: "Piscine C",
        description: "Pointers and if/else structures. The concept of memory address is introduced here.",
        rank: "Week 1",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c02",
        title: "C02",
        level: 0,
        rating: 4.1,
        category: "Piscine C",
        description: "Strings and arrays. Introduction to standard string manipulation functions.",
        rank: "Week 1",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c03",
        title: "C03",
        level: 0,
        rating: 3.9,
        category: "Piscine C",
        description: "More string manipulation. Standard library string functions recreation.",
        rank: "Week 1",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c04",
        title: "C04",
        level: 0,
        rating: 4.0,
        category: "Piscine C",
        description: "Parsing and string conversion (atoi, putnbr).",
        rank: "Week 1",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c05",
        title: "C05",
        level: 0,
        rating: 3.8,
        category: "Piscine C",
        description: "Mathematical functions (factorial, power, fibonacci, sqrt, is_prime).",
        rank: "Week 2",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c06",
        title: "C06",
        level: 0,
        rating: 4.2,
        category: "Piscine C",
        description: "Command line arguments (argc, argv). Sorting command line arguments.",
        rank: "Week 2",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c07",
        title: "C07",
        level: 0,
        rating: 4.4,
        category: "Piscine C",
        description: "Memory allocation (malloc, free). Creating strings and arrays dynamically.",
        rank: "Week 2",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c08",
        title: "C08",
        level: 0,
        rating: 4.1,
        category: "Piscine C",
        description: "Header files and structures. Defining types and macros.",
        rank: "Week 2",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c09",
        title: "C09",
        level: 0,
        rating: 4.5,
        category: "Piscine C",
        description: "Makefile and library creation. Organizing code.",
        rank: "Week 2",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c10",
        title: "C10",
        level: 0,
        rating: 3.5,
        category: "Piscine C",
        description: "File manipulation and recreating simple shell commands like cat and tail.",
        rank: "Week 3",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c11",
        title: "C11",
        level: 0,
        rating: 4.6,
        category: "Piscine C",
        description: "Function pointers. Map, foreach, sort with function pointers.",
        rank: "Week 3",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c12",
        title: "C12",
        level: 0,
        rating: 4.0,
        category: "Piscine C",
        description: "Linked lists. Creating, managing, and sorting linked lists.",
        rank: "Week 3",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "c13",
        title: "C13",
        level: 0,
        rating: 4.8,
        category: "Piscine C",
        description: "Binary trees. The final boss of the Piscine C.",
        rank: "Week 3",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    }
];

/*
export const outerCircleProjects = [
    // Web
    {
        id: "matcha",
        title: "Matcha",
        level: 7,
        rating: 4.5,
        category: "Web",
        description: "An advanced dating website. Handle user profiles, geolocation, tagging, real-time notifications, and matching algorithms.",
        rank: "Web Development",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "hypertube",
        title: "Hypertube",
        level: 7,
        rating: 4.6,
        category: "Web",
        description: "A video streaming application providing a web interface for BitTorrent protocol to stream videos instantly.",
        rank: "Web Development",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "red_tetris",
        title: "Red Tetris",
        level: 8,
        rating: 4.3,
        category: "Web",
        description: "A multiplayer Tetris game played in the browser, focusing on Redux/React state management and WebSockets.",
        rank: "Web Development",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Mobile
    {
        id: "swifty_companion",
        title: "Swifty Companion",
        level: 7,
        rating: 4.2,
        category: "Mobile",
        description: "An iOS application that acts as a mobile interface for the 42 Intranet, displaying student profiles and skills.",
        rank: "Mobile Development",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Security
    {
        id: "snow_crash",
        title: "SnowCrash",
        level: 6,
        rating: 4.8,
        category: "Security",
        description: "An introductory CTF. Exploit vulnerabilities like buffer overflows and script injection to gain privileges.",
        rank: "Security",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "rainfall",
        title: "RainFall",
        level: 7,
        rating: 4.9,
        category: "Security",
        description: "Advanced binary exploitation. Focuses on reverse engineering, bypassing protections, and deep memory corruption.",
        rank: "Security",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Unix/Kernel
    {
        id: "42sh",
        title: "42sh",
        level: 8,
        rating: 4.7,
        category: "Unix",
        description: "The ultimate shell project. Write a fully functional shell supporting job control, logical operators, and piping.",
        rank: "Unix & Kernel",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "malloc",
        title: "Malloc",
        level: 7,
        rating: 3.9,
        category: "Unix",
        description: "Write a comprehensive dynamic memory allocator (malloc, free, realloc) managing memory blocks and optimization.",
        rank: "Unix & Kernel",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "ft_linux",
        title: "ft_linux",
        level: 9,
        rating: 5.0,
        category: "Kernel",
        description: "Building a customized Linux Kernel from scratch. Learn how the OS boots and how to configure a kernel.",
        rank: "Unix & Kernel",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // AI & Algo
    {
        id: "gomoku",
        title: "Gomoku",
        level: 8,
        rating: 4.4,
        category: "AI",
        description: "Create an AI to play Gomoku using Minimax, Alpha-Beta pruning, and heuristic evaluations.",
        rank: "AI & Algorithms",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "n_puzzle",
        title: "N-Puzzle",
        level: 7,
        rating: 4.1,
        category: "Algo",
        description: "Solve the sliding tile puzzle using the A* search algorithm and various heuristics.",
        rank: "AI & Algorithms",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // Graphics
    {
        id: "humangl",
        title: "HumanGL",
        level: 7,
        rating: 4.5,
        category: "Graphics",
        description: "Use OpenGL/Vulkan to create an articulated 3D hierarchal model and animate it.",
        rank: "Graphics 3D",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    {
        id: "scop",
        title: "Scop",
        level: 6,
        rating: 4.0,
        category: "Graphics",
        description: "Introduction to modern OpenGL. Parse .obj files and render 3D models with textures.",
        rank: "Graphics 3D",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    },
    // DevOps
    {
        id: "cloud_1",
        title: "Cloud-1",
        level: 7,
        rating: 3.8,
        category: "DevOps",
        description: "Introduction to cloud infrastructure, container orchestration, and deploying scalable applications.",
        rank: "DevOps",
        feedbacks: generateFeedbacks(), completedBy: generateCompletedBy(), inProgressBy: generateInProgress()
    }
];
*/

export const outerCircleProjects = [
    {
        id: "coming-soon",
        title: "Work in Progress",
        level: "?",
        rating: 0,
        category: "Future",
        description: "The Outer Circle projects are currently under construction. We are working hard to bring you comprehensive guides for Advanced Web, Systems Security, AI, and Graphics development.",
        rank: "Under Construction",
        feedbacks: []
    }
];
